import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, Box, Typography, Button, Switch, Chip, TextField,
  useTheme, useMediaQuery
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { ACTIVITY_TAGS, validateTagSelection } from '../../utils/onboardingTags';
import { saveOnboardingPreferences, markOnboardingCompleted } from '../../services/onboarding/savePreferences';

const PRIMARY = '#5B5EE8';
const PRIMARY_DARK = '#4348C4';

// ─── Shared sub-components ───────────────────────────────────────────────────

const ProgressDots = ({ total, current }) => (
  <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
    {Array.from({ length: total }).map((_, i) => (
      <Box
        key={i}
        sx={{
          width: i === current ? 24 : 8,
          height: 8,
          borderRadius: 4,
          bgcolor: i === current ? PRIMARY : '#CBD5E1',
          transition: 'all 0.3s ease',
        }}
      />
    ))}
  </Box>
);

const MapPin = ({ label, color, x, y }) => (
  <Box
    sx={{
      position: 'absolute',
      left: x,
      top: y,
      transform: 'translate(-50%, -100%)',
      bgcolor: color,
      color: 'white',
      fontSize: 11,
      fontWeight: 700,
      px: '8px',
      py: '3px',
      borderRadius: '8px',
      whiteSpace: 'nowrap',
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -5,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 0,
        height: 0,
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderTop: `5px solid ${color}`,
      },
    }}
  >
    {label}
  </Box>
);

const StepTopBar = ({ onSkip, stepIndex }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 2.5, pb: 1.5 }}>
    <Typography
      onClick={onSkip}
      sx={{ fontSize: 13, color: '#64748B', cursor: 'pointer', userSelect: 'none', '&:hover': { color: PRIMARY } }}
    >
      ข้าม / Skip
    </Typography>
    <ProgressDots total={3} current={stepIndex} />
    <Box sx={{ width: 60 }} />
  </Box>
);

const NextButton = ({ onClick, label, isMobile }) => (
  <Box sx={{
    px: 3,
    pb: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 3,
    pt: 1
  }}>
    <Button
      fullWidth
      variant="contained"
      onClick={onClick}
      sx={{
        bgcolor: PRIMARY,
        fontWeight: 700,
        fontSize: 15,
        py: 1.5,
        borderRadius: 2,
        textTransform: 'none',
        '&:hover': { bgcolor: PRIMARY_DARK },
      }}
    >
      {label}
    </Button>
  </Box>
);

// ─── Screen components ────────────────────────────────────────────────────────

const SplashScreen = ({ language, onLangSelect, onGetStarted, onSignIn, onBrowse }) => (
  <Box
    sx={{
      background: 'linear-gradient(160deg, #6366F1 0%, #4F46E5 50%, #4338CA 100%)',
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100%',
    }}
  >
    {/* Purple hero */}
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        pt: 6,
        pb: 4,
      }}
    >
      <Typography sx={{ fontSize: 52, fontWeight: 900, color: 'white', letterSpacing: -1, mb: 0.5, lineHeight: 1 }}>
        <Box component="span" sx={{ color: '#FBBF24' }}>T</Box>ravy
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13, mb: 4, textAlign: 'center' }}>
        ค้นพบกิจกรรม · Discover events · 发现活动
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
        {[
          { label: '🍜 Street food', color: '#E84949' },
          { label: '🎵 Concert', color: '#7B5CE8' },
          { label: '⛩ Temple', color: '#2D9D5B' },
        ].map(b => (
          <Box
            key={b.label}
            sx={{
              bgcolor: 'white',
              color: b.color,
              fontSize: 13,
              fontWeight: 700,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
            }}
          >
            {b.label}
          </Box>
        ))}
      </Box>
    </Box>

    {/* White card */}
    <Box sx={{ bgcolor: 'white', borderRadius: '24px 24px 0 0', px: 3, pt: 3, pb: 4 }}>
      <Typography sx={{ fontSize: 12, color: '#64748B', mb: 1.5 }}>
        เลือกภาษา / Language / 语言
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
        {[
          { code: 'TH', label: 'TH ไทย' },
          { code: 'EN', label: 'GB English' },
          { code: 'CN', label: 'CN 中文' },
        ].map(lang => (
          <Button
            key={lang.code}
            onClick={() => onLangSelect(lang.code)}
            variant={language === lang.code ? 'contained' : 'outlined'}
            size="small"
            sx={{
              flex: 1,
              fontSize: 12,
              fontWeight: language === lang.code ? 700 : 500,
              bgcolor: language === lang.code ? PRIMARY : 'transparent',
              borderColor: language === lang.code ? PRIMARY : '#CBD5E1',
              color: language === lang.code ? 'white' : '#64748B',
              textTransform: 'none',
              borderRadius: 2,
              '&:hover': {
                bgcolor: language === lang.code ? PRIMARY_DARK : '#F1F5F9',
                borderColor: PRIMARY,
              },
            }}
          >
            {lang.label}
          </Button>
        ))}
      </Box>
      <Button
        fullWidth
        variant="contained"
        onClick={onGetStarted}
        sx={{
          bgcolor: PRIMARY,
          fontWeight: 700,
          fontSize: 15,
          py: 1.5,
          borderRadius: 2,
          mb: 2,
          textTransform: 'none',
          boxShadow: `0 4px 15px ${PRIMARY}50`,
          '&:hover': { bgcolor: PRIMARY_DARK },
        }}
      >
        ดำเนินการต่อ · Continue
      </Button>
      <Box textAlign="center">
        <Typography
          component="span"
          onClick={onBrowse}
          sx={{ fontSize: 13, color: '#94A3B8', cursor: 'pointer', userSelect: 'none', '&:hover': { color: PRIMARY } }}
        >
          ข้ามไปก่อน · Browse without account
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ActivityTagsScreen = ({ onSkip, onNext, selectedTags, setSelectedTags, language }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTagToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      setSelectedTags(selectedTags.filter(id => id !== tagId));
    } else {
      if (selectedTags.length < 10) {
        setSelectedTags([...selectedTags, tagId]);
      }
    }
  };

  const validation = validateTagSelection(selectedTags);
  const canContinue = validation.valid;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepTopBar onSkip={onSkip} stepIndex={0} />
      <Box sx={{ flex: 1, px: 3, pt: 0.5, overflow: 'auto', pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 1.5 }}>01 / 03</Typography>

        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1.3, mb: 0.5 }}>
          {language === 'TH' ? 'เลือกกิจกรรมที่คุณสนใจ' : language === 'CN' ? '选择您感兴趣的活动' : 'Select Activities You Like'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 0.5 }}>
          {language === 'TH' ? 'เลือก 2-10 กิจกรรมที่คุณชื่นชอบ' : language === 'CN' ? '选择 2-10 个您喜欢的活动' : 'Choose 2-10 activities you enjoy'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6, mb: 2 }}>
          {language === 'TH' ? 'เราจะใช้ข้อมูลนี้เพื่อแนะนำกิจกรรมที่เหมาะกับคุณ' : language === 'CN' ? '我们将使用此信息为您推荐合适的活动' : 'We\'ll use this to recommend events you\'ll love'}
        </Typography>

        {/* Selected count */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          p: 1.5,
          bgcolor: selectedTags.length >= 2 && selectedTags.length <= 10 ? '#EEF2FF' : '#FEF2F2',
          borderRadius: 2,
          border: `1px solid ${selectedTags.length >= 2 && selectedTags.length <= 10 ? PRIMARY : '#FCA5A5'}`
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
            {language === 'TH' ? 'เลือกแล้ว:' : language === 'CN' ? '已选择:' : 'Selected:'}
          </Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: PRIMARY }}>
            {selectedTags.length}/10
          </Typography>
        </Box>

        {/* Tag grid */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: 1.5
        }}>
          {ACTIVITY_TAGS.map((tag) => {
            const isSelected = selectedTags.includes(tag.id);
            const isDisabled = !isSelected && selectedTags.length >= 10;

            return (
              <Chip
                key={tag.id}
                label={tag.label[language] || tag.label.EN}
                onClick={() => !isDisabled && handleTagToggle(tag.id)}
                sx={{
                  fontSize: 12,
                  fontWeight: isSelected ? 700 : 500,
                  bgcolor: isSelected ? PRIMARY : '#F8FAFC',
                  color: isSelected ? 'white' : '#64748B',
                  border: `1.5px solid ${isSelected ? PRIMARY : '#E2E8F0'}`,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isSelected ? PRIMARY_DARK : isDisabled ? '#F8FAFC' : '#EEF2FF',
                    borderColor: isSelected ? PRIMARY_DARK : isDisabled ? '#E2E8F0' : PRIMARY,
                    transform: isDisabled ? 'none' : 'translateY(-2px)',
                    boxShadow: isDisabled ? 'none' : `0 4px 12px ${PRIMARY}30`,
                  }
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box sx={{
        px: 3,
        pb: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 3,
        pt: 1
      }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onNext}
          disabled={!canContinue}
          sx={{
            bgcolor: canContinue ? PRIMARY : '#CBD5E1',
            fontWeight: 700,
            fontSize: 15,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': {
              bgcolor: canContinue ? PRIMARY_DARK : '#CBD5E1'
            },
            '&:disabled': {
              color: '#94A3B8'
            }
          }}
        >
          {language === 'TH' ? 'ดำเนินการต่อ' : language === 'CN' ? '继续' : 'Continue'}
        </Button>
        {!canContinue && (
          <Typography sx={{ textAlign: 'center', fontSize: 11, color: '#EF4444', mt: 1 }}>
            {validation.error}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const LifestyleTextScreen = ({ onSkip, onNext, lifestyleText, setLifestyleText, language }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const MAX_CHARS = 500;

  const getPlaceholder = () => {
    switch (language) {
      case 'TH':
        return 'บอกเราเกี่ยวกับตัวคุณ... ชอบทานอาหารประเภทไหน? กิจกรรมที่สนใจ? ไลฟ์สไตล์ของคุณเป็นอย่างไร?\n\nตัวอย่าง: "ชอบอาหารญี่ปุ่นและคาเฟ่บรรยากาศดี ชอบไปเที่ยววัดและพิพิธภัณฑ์ช่วงสุดสัปดาห์"';
      case 'CN':
        return '告诉我们关于您的事... 您喜欢什么样的食物？喜欢什么活动？您的生活方式是什么样的？\n\n例如："喜欢日本料理和氛围好的咖啡馆。周末喜欢参观寺庙和博物馆。"';
      default:
        return 'Tell us about yourself... What kind of food do you like? Activities you enjoy? Your lifestyle preferences?\n\nExample: "I love Japanese food and cozy cafes. Enjoy visiting temples and museums on weekends."';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepTopBar onSkip={onSkip} stepIndex={1} />
      <Box sx={{ flex: 1, px: 3, pt: 0.5, overflow: 'auto', pb: 2 }}>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 1.5 }}>02 / 03</Typography>

        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1.3, mb: 0.5 }}>
          {language === 'TH' ? 'เล่าเกี่ยวกับตัวคุณ' : language === 'CN' ? '告诉我们关于您' : 'Tell Us About You'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 0.5 }}>
          {language === 'TH' ? 'อธิบายความชอบและไลฟ์สไตล์ของคุณ (ไม่บังคับ)' : language === 'CN' ? '描述您的喜好和生活方式（可选）' : 'Describe your preferences and lifestyle (optional)'}
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6, mb: 2.5 }}>
          {language === 'TH' ? 'ยิ่งรายละเอียดมาก เราแนะนำได้แม่นยำยิ่งขึ้น' : language === 'CN' ? '信息越详细，推荐越准确' : 'The more details, the better our recommendations'}
        </Typography>

        <TextField
          multiline
          rows={6}
          fullWidth
          value={lifestyleText}
          onChange={(e) => {
            if (e.target.value.length <= MAX_CHARS) {
              setLifestyleText(e.target.value);
            }
          }}
          placeholder={getPlaceholder()}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: '#F8FAFC',
              '&:hover': {
                bgcolor: '#F1F5F9'
              },
              '&.Mui-focused': {
                bgcolor: 'white',
                '& fieldset': {
                  borderColor: PRIMARY,
                  borderWidth: 2
                }
              }
            },
            '& .MuiOutlinedInput-input': {
              fontSize: 14,
              lineHeight: 1.6,
              color: '#1E293B'
            },
            '& textarea::placeholder': {
              fontSize: 13,
              color: '#94A3B8',
              opacity: 1
            }
          }}
        />

        {/* Character count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
            {language === 'TH' ? 'สามารถเว้นว่างได้หากต้องการ' : language === 'CN' ? '可以留空' : 'Can be left blank'}
          </Typography>
          <Typography sx={{
            fontSize: 11,
            color: lifestyleText.length > MAX_CHARS * 0.9 ? '#EF4444' : '#94A3B8',
            fontWeight: 600
          }}>
            {lifestyleText.length}/{MAX_CHARS}
          </Typography>
        </Box>

        {/* Example prompts */}
        {lifestyleText.length === 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: '#EEF2FF', borderRadius: 2, border: `1px solid ${PRIMARY}30` }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: PRIMARY, mb: 1 }}>
              💡 {language === 'TH' ? 'คำแนะนำ:' : language === 'CN' ? '提示：' : 'Tips:'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748B', lineHeight: 1.6 }}>
              {language === 'TH'
                ? '• ประเภทอาหารที่ชอบ\n• กิจกรรมยามว่าง\n• สไตล์การท่องเที่ยว\n• งบประมาณโดยเฉลี่ย'
                : language === 'CN'
                ? '• 喜欢的食物类型\n• 业余活动\n• 旅行风格\n• 平均预算'
                : '• Food preferences\n• Leisure activities\n• Travel style\n• Budget range'}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{
        px: 3,
        pb: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 3,
        pt: 1
      }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onNext}
          sx={{
            bgcolor: PRIMARY,
            fontWeight: 700,
            fontSize: 15,
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none',
            '&:hover': { bgcolor: PRIMARY_DARK }
          }}
        >
          {language === 'TH' ? 'ดำเนินการต่อ' : language === 'CN' ? '继续' : 'Continue'}
        </Button>
      </Box>
    </Box>
  );
};

const Step1Screen = ({ onSkip, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepTopBar onSkip={onSkip} stepIndex={0} />
      <Box sx={{ flex: 1, px: 3, pt: 0.5, overflow: 'auto' }}>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 1.5 }}>01 / 03</Typography>

      {/* Mock map illustration */}
      <Box
        sx={{
          bgcolor: '#EDE9D0',
          borderRadius: 2,
          height: 155,
          position: 'relative',
          overflow: 'hidden',
          mb: 2.5,
          border: '1px solid #D6D1B0',
        }}
      >
        {[33, 66].map(x => (
          <Box key={x} sx={{ position: 'absolute', top: 0, bottom: 0, left: `${x}%`, width: 1, bgcolor: '#C8C3A0' }} />
        ))}
        {[38, 68].map(y => (
          <Box key={y} sx={{ position: 'absolute', left: 0, right: 0, top: `${y}%`, height: 1, bgcolor: '#C8C3A0' }} />
        ))}
        <MapPin label="🎵 Jazz Night" color="#7B5CE8" x="38%" y="33%" />
        <MapPin label="🍜 Food Tour" color="#E84949" x="64%" y="50%" />
        <MapPin label="⛩ Wat Arun" color="#2D9D5B" x="44%" y="76%" />
        {/* User location dot */}
        <Box
          sx={{
            position: 'absolute',
            width: 10,
            height: 10,
            bgcolor: '#1976D2',
            borderRadius: '50%',
            border: '2px solid white',
            boxShadow: '0 0 0 3px rgba(25,118,210,0.2)',
            top: '60%',
            left: '73%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1.3 }}>
        ค้นหากิจกรรมใกล้คุณ
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: PRIMARY, lineHeight: 1.3, mb: 1.5 }}>
        บนแผนที่แบบเรียลไทม์
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 0.5 }}>
        ดูคอนเสิร์ต ร้านอาหาร วัด และกิจกรรมพิเศษทั้งหมดรอบตัวคุณ — แบบเรียลไทม์ บนแผนที่เดียว
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6, mb: 2 }}>
        See concerts, food, temples & special events around you — all on one live map.
      </Typography>

      {/* Tab chips */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['สำรวจ / Explore', 'กิจกรรม / Events', 'รีวิว / Reviews', 'โปรโมชั่น / Promos'].map((tab, i) => (
          <Box
            key={tab}
            sx={{
              bgcolor: i === 0 ? `${PRIMARY}15` : '#F1F5F9',
              color: i === 0 ? PRIMARY : '#64748B',
              fontSize: 12,
              fontWeight: i === 0 ? 700 : 500,
              px: 1.5,
              py: 0.5,
              borderRadius: '20px',
              border: `1px solid ${i === 0 ? `${PRIMARY}40` : '#E2E8F0'}`,
            }}
          >
            {tab}
          </Box>
        ))}
      </Box>
    </Box>
    <NextButton onClick={onNext} label="ถัดไป · Next" isMobile={isMobile} />
  </Box>
  );
};

const Step2Screen = ({ onSkip, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepTopBar onSkip={onSkip} stepIndex={1} />
      <Box sx={{ flex: 1, px: 3, pt: 0.5, overflow: 'auto' }}>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 1.5 }}>02 / 03</Typography>

      {/* Mock review card */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          overflow: 'hidden',
          mb: 2.5,
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
        }}
      >
        <Box sx={{ bgcolor: '#2D5A3D', height: 65, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography sx={{ fontSize: 26 }}>🌿</Typography>
        </Box>
        <Box sx={{ p: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>Khao-Sō-i Soi Convent Silom</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: '#FBBF24', fontSize: 13 }}>★★★★★</Typography>
            <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>วันที่รีวิว · 01-มี.ค.-26</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ bgcolor: '#EEF2FF', color: PRIMARY, fontSize: 11, fontWeight: 600, px: 1, py: 0.3, borderRadius: 1 }}>
              รีวิว
            </Box>
            <Box
              sx={{
                bgcolor: '#FFF7ED',
                color: '#C2410C',
                fontSize: 11,
                fontWeight: 600,
                px: 1,
                py: 0.3,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#EF4444', display: 'inline-block' }} />
              คูปองส่วนลด 20%
            </Box>
          </Box>
        </Box>

        {/* Coupon row */}
        <Box
          sx={{
            mx: 1.5,
            mb: 1.5,
            bgcolor: '#F8FAFC',
            borderRadius: 1.5,
            border: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
          }}
        >
          <Box
            sx={{
              bgcolor: '#F97316',
              color: 'white',
              fontSize: 11,
              fontWeight: 800,
              px: 0.8,
              py: 0.8,
              borderRadius: 1,
              minWidth: 36,
              textAlign: 'center',
              lineHeight: 1.2,
              flexShrink: 0,
            }}
          >
            ลด<br />20%
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Khao-Sō-i Soi Convent
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748B' }}>สแกน QR ที่ร้าน · Scan QR at shop</Typography>
          </Box>
          <Button
            size="small"
            variant="contained"
            sx={{
              bgcolor: PRIMARY,
              fontSize: 11,
              fontWeight: 700,
              px: 1,
              py: 0.3,
              minWidth: 0,
              textTransform: 'none',
              borderRadius: 1.5,
              flexShrink: 0,
            }}
          >
            ใช้เลย
          </Button>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1.3 }}>
        รีวิวจริง คูปองจริง
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#F97316', lineHeight: 1.3, mb: 1.5 }}>
        จากคนที่ไปมาแล้ว
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 0.5 }}>
        ดูรีวิวและวิดีโอจากนักเดินทางจริง แล้วรับส่วนลดพิเศษที่ร้านค้าพาร์ทเนอร์ทันที
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6 }}>
        Read real reviews, watch creator videos, and unlock exclusive coupons at partner venues.
      </Typography>
    </Box>
    <NextButton onClick={onNext} label="ถัดไป · Next" isMobile={isMobile} />
  </Box>
  );
};

const Step3Screen = ({ onSkip, onNext }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <StepTopBar onSkip={onSkip} stepIndex={2} />
      <Box sx={{ flex: 1, px: 3, pt: 0.5, overflow: 'auto' }}>
        <Typography sx={{ fontSize: 12, color: '#94A3B8', fontWeight: 600, mb: 1.5 }}>03 / 03</Typography>

      {/* Mock event cards */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
        {[
          { name: 'YANO x TONG GLUEA', date: '21-ก.พ.-25 — 18-เม.ย.-25', status: 'กำลังมา', statusColor: PRIMARY, statusBg: '#EEF2FF' },
          { name: 'ONE NIGHT WATER FRESH', date: '02-Apr — 05-Apr', status: 'สิ้นสุดแล้ว', statusColor: '#C2410C', statusBg: '#FFF7ED' },
        ].map(ev => (
          <Box
            key={ev.name}
            sx={{
              flex: 1,
              bgcolor: 'white',
              borderRadius: 2,
              border: '1px solid #E2E8F0',
              p: 1.5,
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.3, mb: 0.5 }}>{ev.name}</Typography>
            <Typography sx={{ fontSize: 10, color: '#94A3B8', mb: 0.5 }}>{ev.date}</Typography>
            <Box
              sx={{
                bgcolor: ev.statusBg,
                color: ev.statusColor,
                fontSize: 10,
                fontWeight: 700,
                px: 1,
                py: 0.3,
                borderRadius: 1,
                display: 'inline-block',
              }}
            >
              {ev.status}
            </Box>
          </Box>
        ))}
      </Box>

      {/* Add event row */}
      <Box
        sx={{
          bgcolor: 'white',
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2.5,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: PRIMARY,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 800,
            fontSize: 14,
            flexShrink: 0,
          }}
        >
          B
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700 }}>เพิ่มกิจกรรมใหม่</Typography>
          <Typography sx={{ fontSize: 11, color: '#64748B' }}>แบ่งปันกับชุมชน · Share with community</Typography>
        </Box>
        <Button
          size="small"
          variant="contained"
          sx={{ bgcolor: PRIMARY, fontSize: 12, fontWeight: 700, px: 1.5, py: 0.5, textTransform: 'none', borderRadius: 1.5, flexShrink: 0 }}
        >
          + เพิ่ม
        </Button>
      </Box>

      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1E293B', lineHeight: 1.3 }}>
        ติดตามเทรนด์
      </Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#10B981', lineHeight: 1.3, mb: 1.5 }}>
        และแบ่งปันกิจกรรม
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#475569', lineHeight: 1.6, mb: 0.5 }}>
        ดูกิจกรรมที่กำลังมาถึง ติดตามเทรนด์ และช่วยกันเพิ่มข้อมูลกิจกรรมให้ชุมชน
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.6 }}>
        Track upcoming events, follow trends, and add events to help fellow travelers.
      </Typography>
    </Box>
    <NextButton onClick={onNext} label="เริ่มเลย · Let's go" isMobile={isMobile} />
  </Box>
  );
};

const PermissionsScreen = ({ locationOn, setLocationOn, cameraOn, setCameraOn, notifOn, setNotifOn, onAllow, onLater, isSaving }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ px: 3, pt: 4, pb: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1E293B', mb: 0.5 }}>
        การเข้าถึงที่แอปอาจขอ
      </Typography>
      <Typography sx={{ fontSize: 13, color: '#475569', mb: 0.3 }}>
        Travy อาจขอสิทธิ์เหล่านี้เพื่อประสบการณ์และฟังก์ชันที่ดีขึ้น
      </Typography>
      <Typography sx={{ fontSize: 12, color: '#10B981', mb: 3 }}>
        This app may request these permissions for better experience and functionality.
      </Typography>

      {[
        {
          icon: '📍',
          label: 'ตำแหน่งที่ตั้ง · Location',
          desc: 'ค้นหากิจกรรมใกล้คุณ และแชร์ตำแหน่งกับกลุ่มทัวร์ / Find nearby events and share position with tour group members',
        },
        {
          icon: '📷',
          label: 'กล้อง · Camera',
          desc: 'ถ่ายภาพสำหรับรีวิว แชร์ประสบการณ์ และแชทกับผู้ใช้ / Take photos for reviews, share experiences, and chat with users',
        },
        {
          icon: '🔔',
          label: 'การแจ้งเตือน · Notifications',
          desc: 'รับการแจ้งเตือนกิจกรรม โปรโมชั่น และข้อความ / Receive event updates, promotions, and messages',
        },
      ].map(p => (
        <Box
          key={p.label}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: '#F8FAFC',
            borderRadius: 2,
            p: 1.5,
            mb: 1.5,
            border: '1px solid #E2E8F0',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#EEF2FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            {p.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>{p.label}</Typography>
            <Typography sx={{ fontSize: 11, color: '#64748B' }}>{p.desc}</Typography>
          </Box>
        </Box>
      ))}
    </Box>

    <Box sx={{
      paddingBottom: isMobile ? 'calc(24px + env(safe-area-inset-bottom))' : 0
    }}>
      <Button
        fullWidth
        variant="contained"
        onClick={onAllow}
        disabled={isSaving}
        sx={{
          bgcolor: isSaving ? '#CBD5E1' : PRIMARY,
          fontWeight: 700,
          fontSize: 15,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          '&:hover': { bgcolor: isSaving ? '#CBD5E1' : PRIMARY_DARK },
          '&:disabled': {
            color: '#94A3B8'
          }
        }}
      >
        {isSaving ? 'กำลังบันทึก... · Saving...' : 'เข้าใช้งาน · Continue to App'}
      </Button>
    </Box>
  </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const WelcomeModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { language, changeLanguage } = useLanguage();

  const [screen, setScreen] = useState('splash');
  const [selectedTags, setSelectedTags] = useState([]);
  const [lifestyleText, setLifestyleText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [locationOn, setLocationOn] = useState(true);
  const [cameraOn, setCameraOn] = useState(true);
  const [notifOn, setNotifOn] = useState(false);

  // Reset to splash each time the modal opens
  useEffect(() => {
    if (open) {
      setScreen('splash');
      setSelectedTags([]);
      setLifestyleText('');
      setIsSaving(false);
    }
  }, [open]);

  const handleSkip = () => setScreen('permissions');

  const handleTagsContinue = () => {
    const validation = validateTagSelection(selectedTags);
    if (validation.valid) {
      setScreen('lifestyle');
    }
  };

  const handleLifestyleContinue = () => {
    setScreen('permissions');
  };

  const handleGetStarted = async () => {
    setIsSaving(true);

    try {
      // Save preferences
      const result = await saveOnboardingPreferences(selectedTags, lifestyleText, language);

      if (result.success) {
        console.log('✅ Preferences saved successfully');
      } else {
        console.warn('⚠️ Failed to save preferences:', result.error);
      }

      // Mark onboarding as completed
      markOnboardingCompleted();

      // Request location permission
      if (locationOn) {
        navigator.geolocation.getCurrentPosition(() => {}, () => {});
      }

      // Close modal and pass data to parent
      onClose(selectedTags, lifestyleText);
    } catch (error) {
      console.error('❌ Error during onboarding:', error);
      // Still close the modal even if save failed
      markOnboardingCompleted();
      onClose(selectedTags, lifestyleText);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          maxHeight: isMobile
            ? 'calc(100dvh - env(safe-area-inset-bottom))'
            : '88vh',
          paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : 0,
          m: isMobile ? 0 : 2,
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {screen === 'splash' && (
          <SplashScreen
            language={language}
            onLangSelect={changeLanguage}
            onGetStarted={() => setScreen('tags')}
            onSignIn={() => { onClose(); navigate('/login'); }}
            onBrowse={() => onClose()}
          />
        )}
        {screen === 'tags' && (
          <ActivityTagsScreen
            onSkip={handleSkip}
            onNext={handleTagsContinue}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            language={language}
          />
        )}
        {screen === 'lifestyle' && (
          <LifestyleTextScreen
            onSkip={handleSkip}
            onNext={handleLifestyleContinue}
            lifestyleText={lifestyleText}
            setLifestyleText={setLifestyleText}
            language={language}
          />
        )}
        {screen === 'step1' && (
          <Step1Screen onSkip={handleSkip} onNext={() => setScreen('step2')} />
        )}
        {screen === 'step2' && (
          <Step2Screen onSkip={handleSkip} onNext={() => setScreen('step3')} />
        )}
        {screen === 'step3' && (
          <Step3Screen onSkip={handleSkip} onNext={() => setScreen('permissions')} />
        )}
        {screen === 'permissions' && (
          <PermissionsScreen
            locationOn={locationOn}
            setLocationOn={setLocationOn}
            cameraOn={cameraOn}
            setCameraOn={setCameraOn}
            notifOn={notifOn}
            setNotifOn={setNotifOn}
            onAllow={handleGetStarted}
            onLater={() => onClose()}
            isSaving={isSaving}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default WelcomeModal;
